import React, { useState } from "react";
import { Box, Button, Callout, Dialog, Heading, Intent, Label, Radio, Text } from "@blasterjs/core";

import { DialogBody, DialogFooter, DialogHeader } from "./DialogLayout";
import ReasonForChange from "./ReasonForChange";
import { AdjudicationMode, AdjudicationParadigm } from "../models";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  recordCaseAdjudication,
  recordImageAdjudication,
  switchAdjudicatorRead
} from "../slices/caseImageViewer";

/**
 * FE-06: the Adjudicator's read-switching + selection panel. Also serves System Admin / Study Admin /
 * ISC (LUC-278) when the reads come back unblinded (`blinded = false`): the same reader switcher,
 * labelled with each CR's name, read-only (no "My read", no selection action).
 *
 *  - FORCED: read-only. Radio switches between each CR's read (blinded "Reader N"). A "Select this
 *    read" action records the winner — for the whole case (CASE_LEVEL) or the current biopsy image
 *    (BIOPSY_LEVEL). Current selection is shown.
 *  - HYBRID: radio switches between each CR's read (read-only) and the adjudicator's OWN read
 *    (annotate-able; "Mark as Scored" lives on the Overview panel).
 *  - INDEPENDENT: the viewer behaves like a normal reader (no switcher needed) — this panel is not
 *    shown for INDEPENDENT.
 *
 * In the adjudicator's view no CR usernames are ever shown — only the blinded "Reader N" labels from
 * the backend.
 */
const SidebarPanelAdjudication = () => {
  const dispatch = useAppDispatch();
  const adjudicationReads = useAppSelector(state => state.caseImageViewer.adjudicationReads);
  const selectedCrUserId = useAppSelector(state => state.caseImageViewer.selectedCrUserId);
  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);
  const studyView = useAppSelector(state => state.studies.study);

  const study = "resource" in studyView ? studyView.resource.study : null;
  const paradigm = study?.adjudicationParadigm ?? null;
  const mode = study?.adjudicationMode ?? AdjudicationMode.CaseLevel;

  const isOversight = adjudicationReads !== null && !adjudicationReads.blinded;
  const isForced = paradigm === AdjudicationParadigm.Forced;
  const isHybrid = paradigm === AdjudicationParadigm.Hybrid;
  const hasOwnRead =
    isHybrid && adjudicationReads !== null && adjudicationReads.adjudicatorAssignmentId !== null;

  // FE-06: the initial read default lives in the slice (adjudicationReadsFetch.fulfilled): Hybrid
  // starts on the adjudicator's OWN read (My Read / null), Forced on Reader 1, and a still-valid
  // selection is preserved across re-fetches. It is NOT done here: this panel unmounts when the
  // adjudicator switches to another sidebar tab (only the active tab is rendered) and remounts on
  // return, so a component-local "default once" effect would re-fire on every remount and clobber a
  // valid selection back to My Read (LUC-265).

  if (adjudicationReads === null || paradigm === null) {
    return null;
  }

  const currentImageId =
    selectedImage && "resource" in selectedImage
      ? selectedImage.resource.imageAndQuery.image.id
      : null;

  // The winning assignment for the current context (case-level, or the current image's biopsy).
  const currentSelection =
    mode === AdjudicationMode.BiopsyLevel
      ? currentImageId
        ? adjudicationReads.imageSelections[currentImageId] ?? null
        : null
      : adjudicationReads.caseSelection;

  const onSwitch = (crUserId: string | null) => () => dispatch(switchAdjudicatorRead(crUserId));

  // Changing an already-recorded selection is a GxP decision-of-record change: capture a reason via
  // a confirm dialog before dispatching. A first-time selection (or re-affirming the same read) goes
  // straight through with no reason, matching the backend's reason-required-on-change rule.
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonForChange, setReasonForChange] = useState("");

  const record = (assignmentId: string, reason?: string) => {
    if (mode === AdjudicationMode.BiopsyLevel && currentImageId) {
      void dispatch(
        recordImageAdjudication({
          imageId: currentImageId,
          winningAssignmentId: assignmentId,
          reasonForChange: reason
        })
      );
    } else {
      void dispatch(
        recordCaseAdjudication({ winningAssignmentId: assignmentId, reasonForChange: reason })
      );
    }
  };

  // A change is one that overwrites an existing selection with a DIFFERENT winner.
  const isChange = (assignmentId: string) =>
    currentSelection !== null && currentSelection !== assignmentId;

  const onSelect = (assignmentId: string) => () => {
    if (isChange(assignmentId)) {
      setReasonForChange("");
      setReasonDialogOpen(true);
    } else {
      record(assignmentId);
    }
  };

  const selectedAssignmentId =
    adjudicationReads.reads.find(r => r.crUserId === selectedCrUserId)?.assignmentId ?? "";

  const onConfirmChange = () => {
    record(selectedAssignmentId, reasonForChange);
    setReasonDialogOpen(false);
  };

  const onChangeReason = (e: React.ChangeEvent<HTMLInputElement>) =>
    setReasonForChange(e.target.value);

  const selectionLabel = adjudicationReads.reads.find(r => r.assignmentId === currentSelection)
    ?.label;

  return (
    <Box p={3}>
      {/* Match the other sidebar panels' typography: h5 panel title, h6 section headings, and 14px
          body text (see SidebarPanelAnnotations/SidebarPanelQueries and SidebarPanelCaseDetails). */}
      <Heading as="h5">{isOversight ? "Central Reader Reads" : "Adjudication"}</Heading>

      {isOversight ? (
        <Box mt={2}>
          <Text fontSize="14px">
            Choose a Central Reader to view their annotations and scores on this image.
          </Text>
        </Box>
      ) : !adjudicationReads.allReadsComplete ? (
        <Box mt={2}>
          <Callout intent={Intent.PRIMARY}>
            <Text fontSize="14px">
              All Central Reader reads must be complete before a selection can be recorded.
            </Text>
          </Callout>
        </Box>
      ) : null}

      <Box mt={3}>
        <Heading as="h6">Reads</Heading>
        {/* blasterJS Radio renders no label of its own — wrap it in a Label with a sibling Text, the
            pattern used by RespondQueryDialog / CreateQueryFlow. */}
        {hasOwnRead ? (
          <Label style={{ display: "flex", cursor: "pointer", gap: "10px" }} mb={1}>
            <Radio checked={selectedCrUserId === null} onChange={onSwitch(null)} />
            <Text fontSize="14px">My read</Text>
          </Label>
        ) : null}
        {adjudicationReads.reads.map(read => (
          <Label
            key={read.crUserId}
            style={{ display: "flex", cursor: "pointer", gap: "10px" }}
            mb={1}
          >
            <Radio
              checked={selectedCrUserId === read.crUserId}
              onChange={onSwitch(read.crUserId)}
            />
            <Text fontSize="14px">
              {`${read.label}${read.readCompleted ? "" : " (in progress)"}`}
            </Text>
          </Label>
        ))}
      </Box>

      {isForced ? (
        <Box mt={3}>
          <Heading as="h6">Selection</Heading>
          {currentSelection ? (
            <Text fontSize="14px">
              Selected read: <b>{selectionLabel ?? "—"}</b>
              {mode === AdjudicationMode.BiopsyLevel ? " (this biopsy)" : " (case)"}
            </Text>
          ) : (
            <Text fontSize="14px">No read selected yet.</Text>
          )}
          {selectedCrUserId !== null && !isOversight ? (
            <Box mt={2}>
              <Button
                appearance="prominent"
                intent="primary"
                disabled={
                  !adjudicationReads.allReadsComplete || currentSelection === selectedAssignmentId
                }
                onClick={onSelect(selectedAssignmentId)}
              >
                {currentSelection !== null && currentSelection !== selectedAssignmentId
                  ? "Change selection to this read"
                  : "Select this read"}
              </Button>
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Dialog
        isOpen={reasonDialogOpen}
        onRequestClose={() => setReasonDialogOpen(false)}
        appElementId="root"
      >
        <DialogHeader
          title="Change recorded selection"
          closeDialog={() => setReasonDialogOpen(false)}
        />
        <DialogBody>
          <Box>
            <Text fontSize="14px">
              This {mode === AdjudicationMode.BiopsyLevel ? "biopsy" : "case"} already has a
              recorded adjudication selection. Changing it updates the decision of record; a reason
              for change is required.
            </Text>
          </Box>
          <Box style={{ marginTop: 12 }}>
            Reason for Change*
            <ReasonForChange
              rkey="adjudicationSelection_reason"
              value={reasonForChange}
              onChange={onChangeReason}
            />
          </Box>
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={onConfirmChange}
            disabled={reasonForChange.trim() === ""}
            appearance="prominent"
            intent="danger"
          >
            Confirm change
          </Button>
          <Button onClick={() => setReasonDialogOpen(false)}>Cancel</Button>
        </DialogFooter>
      </Dialog>
    </Box>
  );
};

export default SidebarPanelAdjudication;
